import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, Phone, Mail, MapPin } from 'lucide-react';
import MDVIPCallToAction from '../components/MDVIPCallToAction';
import AnimatedPage from '../components/AnimatedPage';

const MDVIPServices = () => {
  return (
    <AnimatedPage className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-stone-50 via-stone-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-stone-900 mb-6">
              Why Choose an MDVIP Affiliated Practice ?
            </h1>
            <div className="h-1 w-32 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 mx-auto mb-8"></div>
            <p className="text-base md:text-lg text-stone-700 leading-relaxed max-w-4xl mx-auto text-left">
              Many modern medical practices are structured hierarchically, meaning new patients primarily see mid-level staff while direct physician time is reserved only for a select few. An MDVIP-affiliated practice completely flips this model. We don't employ mid-level providers; instead, every patient receives equal, direct access to a board-certified MD. This is the hallmark of the premier MDVIP model of care—uncompromising, doctor-led care for everyone. And patients immediately feel this difference.
            </p>
          </div>
        </div>
      </section>

      {/* MDVIP Benefits Section */}
      <section className="pt-8 pb-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Small Heading */}
          <h2 className="text-2xl md:text-3xl font-bold text-stone-900 text-center mb-8">
            Benefits at a Glance
          </h2>
          
          {/* MDVIP Infographic */}
          <div className="max-w-3xl mx-auto mb-16">
            <img
              src="https://customer-assets.emergentagent.com/job_premium-med-care/artifacts/r8wja9rp_ChatGPT%20Image%20Jun%2026%2C%202026%2C%2002_13_42%20AM.png"
              alt="MDVIP Benefits Diagram"
              className="w-full h-auto rounded-lg shadow-lg"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Availability Benefits */}
            <Card className="glass-card-strong shadow-strong card-premium">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-stone-900 mb-4 text-center">
                  Availability Benefits
                </h3>
                <ul className="space-y-3">
                  {[
                    "24/7 by phone – text, call or e-mail",
                    "Direct communication with area hospitals on your behalf",
                    "Same day or next day appointments"
                  ].map((benefit) => (
                    <li key={benefit} className="flex items-start space-x-2">
                      <Check className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-stone-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* MDVIP Network Benefits */}
            <Card className="glass-card-strong shadow-strong card-premium">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-stone-900 mb-4 text-center">
                  MDVIP Network Benefits
                </h3>
                <ul className="space-y-3">
                  {[
                    "Access to medical centers of excellence for second opinion specialist consultation",
                    "Family health benefits at no additional cost for members aged 5 to 26",
                    "Travel benefits within the MDVIP network of affiliated physicians"
                  ].map((benefit) => (
                    <li key={benefit} className="flex items-start space-x-2">
                      <Check className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-stone-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Annual Wellness Program */}
            <Card className="glass-card-strong shadow-strong card-premium">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-stone-900 mb-4 text-center">
                  Annual Wellness Program
                </h3>
                <ul className="space-y-3">
                  {[
                    "Proactive prevention that can possibly predict problems or see early signs",
                    "Personalized for each patient based on family history, health, etc.",
                    "Extensive screenings and testing that covers you from head to toe",
                    "Gives The doctor a baseline to help better serve your health needs"
                  ].map((benefit) => (
                    <li key={benefit} className="flex items-start space-x-2">
                      <Check className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-stone-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* MDVIP CTA */}
      <MDVIPCallToAction />
    </AnimatedPage>
  );
};

export default MDVIPServices;
